// ─── Theme-system (Design Sprint E) ──────────────────────────────────────────

function hexToHsl(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0;
  if (max !== min) {
    const d = max - min;
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return h * 360;
}

const WARM_THEME = {
  sidebar_bg:          '#0A0706',
  sidebar_active_bg:   '#1A1410',
  sidebar_text:        '#6B5D52',
  sidebar_text_active: '#F0E8E0',
  sidebar_text_muted:  '#4A3F36',
  sidebar_border:      '#1A1410',
  hero_bg:             '#0A0706',
  hero_border:         '#1A1410',
  text_primary:        '#0A0706',
};

const COOL_THEME = {
  sidebar_bg:          '#06090F',
  sidebar_active_bg:   '#15192A',
  sidebar_text:        '#6B7588',
  sidebar_text_active: '#E8EAF0',
  sidebar_text_muted:  '#4A5266',
  sidebar_border:      '#15192A',
  hero_bg:             '#0A0F1A',
  hero_border:         '#15192A',
  text_primary:        '#0A0F1A',
};

// Gemensam neutral skala (T-10). Används av klientvyer, login och primitiver;
// terapeutens tokens nedan pekar på samma värden så att det finns EN gråskala.
export const NEUTRAL = {
  ink:    '#0F172A',  // rubriker, primär text
  text:   '#3F4756',  // brödtext
  muted:  '#6B7588',  // sekundär text, etiketter
  faint:  '#9AA3B5',  // platshållare, inaktivt
  line:   '#E5E9F0',  // kantlinjer, avdelare
  page:   '#F5F7FA',  // sidbakgrund
  card:   '#FFFFFF',
};

const LOCKED_TOKENS = {
  page_bg:        NEUTRAL.page,
  card_bg:        NEUTRAL.card,
  card_border:    NEUTRAL.line,
  text_secondary: NEUTRAL.muted,
  card_shadow:    '0 1px 3px rgba(10,7,6,0.04)',
  card_radius:    '14px',
  btn_radius:     '6px',
};

// ─── Färgtoner härledda ur klinikens accent (T-16, G1–G3) ────────────────────
// Tidigare fanns bara två fasta ljusa toner (beige/mint), så t.ex. lila och blå
// kliniker fick mintgröna ytor. Nu räknas allt ur accenten och kontrastkrav.
function rgbOf(hex) {
  const n = parseInt(hex.slice(1, 7), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function mixHex(a, b, t) {
  const x = rgbOf(a), y = rgbOf(b);
  return '#' + x.map((v, i) => Math.round(v * (1 - t) + y[i] * t).toString(16).padStart(2, '0')).join('');
}
function relLum(hex) {
  const c = rgbOf(hex).map((v) => { const s = v / 255; return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4); });
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}
export function contrastRatio(a, b) {
  const A = relLum(a), B = relLum(b);
  return (Math.max(A, B) + 0.05) / (Math.min(A, B) + 0.05);
}
// Mörka accenten mot ink tills kontrasten mot `bg` når `min`.
function darkenUntil(acc, bg, min) {
  let out = acc, t = 0;
  while (contrastRatio(out, bg) < min && t < 0.9) { t += 0.05; out = mixHex(acc, '#0F172A', t); }
  return out;
}

export function accentTones(accentHex) {
  const acc = /^#[0-9a-f]{6}$/i.test(accentHex || '') ? accentHex : '#2C513F';
  const softBg = mixHex(acc, '#FFFFFF', 0.88);
  const softText = darkenUntil(acc, softBg, 4.8);
  // Text i accentfärg på vitt (länkar, siffror): accenten om den klarar 4.5:1, annars mörkare variant.
  const text = contrastRatio(acc, '#FFFFFF') >= 4.5 ? acc : darkenUntil(acc, '#FFFFFF', 4.5);
  // Mörk accent syns inte mot mörkt huvud → vit knapp med text i klinikens färg.
  const darkOnHero = contrastRatio(acc, '#0F172A') < 3;
  return {
    soft_bg: softBg,
    soft_text: softText,
    text,
    hero_btn_bg: darkOnHero ? '#FFFFFF' : acc,
    hero_btn_text: darkOnHero ? (contrastRatio(acc, '#FFFFFF') >= 4.5 ? acc : '#0F172A') : onAccent(acc),
    hero_glow: darkOnHero ? mixHex(acc, '#FFFFFF', 0.45) : acc,
  };
}

export function resolveTheme(accentHex) {
  const safe = /^#[0-9a-f]{6}$/i.test(accentHex || '') ? accentHex : '#2C513F';
  const hue = hexToHsl(safe);
  const [r, g, b] = rgbOf(safe);
  const sat = (Math.max(r, g, b) - Math.min(r, g, b)) / 255;
  const warm = sat > 0.12 && hue >= 0 && hue <= 55;
  const base = warm ? WARM_THEME : COOL_THEME;
  const tones = accentTones(safe);
  return {
    ...base,
    ...LOCKED_TOKENS,
    accent:          safe,
    accent_soft_bg:  tones.soft_bg,
    accent_soft_text: tones.soft_text,
    accent_text:     tones.text,
    hero_btn_bg:     tones.hero_btn_bg,
    hero_btn_text:   tones.hero_btn_text,
    hero_glow:       tones.hero_glow,
    accent_muted:    safe + '1a',
  };
}

export const STATUS_COLORS = {
  success:    '#16a34a',
  success_bg: '#dcfce7',
  warning:    '#d97706',
  warning_bg: '#fef3c7',
  error:      '#dc2626',
  error_bg:   '#fee2e2',
  neutral:    '#6b7280',
  neutral_bg: '#f3f4f6',
  info:       '#2F6FDE',
  info_bg:    '#EAF1FD',
};

// ─── Klinios neutrala designpalett (legacy/fallback) ─────────────────────────
// Tenant-accent byts ut av BrandContext – dessa är fallback/default.
export const klinio = {
  sidebarBg:       '#0f172a',
  sidebarActive:   '#1e293b',
  contentBg:       '#f1f5f9',
  surface:         '#f8fafc',
  pageHeader:      '#f1f5f9',
  cardBg:          '#ffffff',
  accent:          '#2C513F',
  textPrimary:     '#0f172a',
  textMuted:       '#64748b',
  textFaint:       '#94a3b8',
  border:          '#e2e8f0',
  borderStrong:    '#cbd5e1',
  cardShadow:      '0 2px 8px rgba(0,0,0,0.06)',
  cardShadowHover: '0 4px 16px rgba(0,0,0,0.10)',
  success:         '#16a34a',
  successBg:       '#f0fdf4',
  successBorder:   '#bbf7d0',
  warning:         '#d97706',
  warningBg:       '#fffbeb',
  error:           '#dc2626',
  infoBg:          '#eff6ff',
  infoBorder:      '#bfdbfe',

  heroBg:             '#0f172a',
  heroText:           '#e2e8f0',
  heroTextMuted:      '#475569',
  heroTextFaint:      '#334155',
  heroBorder:         'rgba(255,255,255,0.08)',
  heroSurface:        'rgba(255,255,255,0.05)',
  cardElevatedShadow: '0 4px 20px rgba(0,0,0,0.07)',
  cardRadius:         '16px',
  cardRadiusSm:       '12px',
  timelineRowBorder:  '#f1f5f9',
  pillBg:             '#f1f5f9',
  pillText:           '#475569',
};

// Läsbar textfärg ovanpå en accentfärg (vit eller ink) – välj den med högst kontrast.
// Behövs eftersom terapeuten väljer accent fritt (t.ex. orange där vit text blir svårläst).
export function onAccent(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return '#FFFFFF';
  const n = parseInt(m[1], 16);
  const lin = (c) => { const s = c / 255; return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4); };
  const L = 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
  const inkL = 0.0089; // #0F172A
  const vsWhite = 1.05 / (L + 0.05);
  const vsInk = (L + 0.05) / (inkL + 0.05);
  return vsInk > vsWhite ? NEUTRAL.ink : '#FFFFFF';
}
